import React, { useEffect, useState } from 'react';
import { ChevronsLeft, ChevronsRight, LogOut, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import userProfile from "assets/images/users/user-profile.png";
import logoSm from "assets/images/logo-sm.png";
import logoDark from "assets/images/logo-dark.png";
import logoLight from "assets/images/logo-light.png";
import { useCompany } from 'hooks/useCompany';

import LightDark from 'Common/LightDark';
import { Dropdown } from 'Common/Components/Dropdown';
import { changeLeftsidebarSizeType } from 'slices/thunk';
import { lockBodyScroll, unlockBodyScroll } from 'utils/bodyScrollLock';
import NotificationDropdown from 'Common/NotificationDropdown';
import DateTimeDisplay from 'components/DateTimeDisplay';
import UpcomingMeetings from 'components/UpcomingMeetings';
import Modal from "Common/Components/Modal";

const Header = ({ handleToggleDrawer, handleDrawer }: any) => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const { companyLogoWide, companyName, isLoading } = useCompany();
    const [logoError, setLogoError] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const [userdata, setUser] = useState({
        firstName: "",
        lastName: "",
        userRole: "",
        userPhoto: ""
    });

    const handleConfirmLogout = () => {
        // Ensure the logout screen always shows the loader.
        sessionStorage.setItem("postLogoutLoader", "1");
        setShowLogoutConfirm(false);
        navigate("/logout");
    };

    useEffect(() => {
        const storedUser = localStorage.getItem("userDetails");
        if (storedUser) {
            const userData = JSON.parse(storedUser);
            setUser({
                firstName: userData.first_name || "Guest",
                lastName: userData.last_name || "",
                userRole: userData.role || "",
                userPhoto: userData.profile_picture || userProfile
            });
        }
    }, []);

    const selectLayoutState = (state: any) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout: any) => ({
            layoutSidebarSizeType: layout.layoutSidebarSizeType,
            layoutType: layout.layoutType,
        })
    );

    const { layoutSidebarSizeType, layoutType } = useSelector(selectLayoutProperties);

    const handleTopbarHamburgerIcon = () => {
        var windowSize = document.documentElement.clientWidth;
        let sidebarOverlay = document.getElementById("sidebar-overlay") as any;

        if (windowSize < 768) {
            lockBodyScroll();
            if (sidebarOverlay.classList.contains("hidden")) {
                sidebarOverlay.classList.remove("hidden");
                (document as Document | any).querySelector(".app-menu").classList.remove("hidden");
            } else {
                sidebarOverlay.classList.add("hidden");
                (document as Document | any).querySelector(".app-menu").classList.add("hidden");
                unlockBodyScroll();
            }
            dispatch(changeLeftsidebarSizeType("lg"));
        } else {
            dispatch(changeLeftsidebarSizeType(layoutSidebarSizeType === "sm" ? "lg" : "sm"));
        }
    }

    useEffect(() => {
        const handleResizeLayout = () => {
            var windowSize = document.documentElement.clientWidth;

            if (windowSize < 768) {
                dispatch(changeLeftsidebarSizeType("lg"));
            } else if (windowSize <= 1024) {
                if (layoutType === "vertical") {
                    dispatch(changeLeftsidebarSizeType("sm"));
                } else {
                    dispatch(changeLeftsidebarSizeType("lg"));
                }
            } else {
                dispatch(changeLeftsidebarSizeType("lg"));
            }
        }

        const outerSideElement = () => {
            var windowSize = document.documentElement.clientWidth;
            var sidebarOverlay = document.getElementById("sidebar-overlay") as any;
            if (sidebarOverlay) {
                sidebarOverlay.addEventListener("click", () => {
                    if (!sidebarOverlay.classList.contains("hidden")) {
                        if (windowSize <= 768) {
                            document?.querySelector(".app-menu")?.classList.add("hidden");
                            unlockBodyScroll();
                            sidebarOverlay.classList.add("hidden");
                        } else {
                            dispatch(changeLeftsidebarSizeType("lg"));
                        }
                    }
                });
            }
        }

        const scrollNavigation = () => {
            var scrollUp = document.documentElement.scrollTop;
            if (scrollUp >= 50) {
                document.getElementById("page-topbar")?.classList.add('is-sticky');
            } else {
                document.getElementById("page-topbar")?.classList.remove('is-sticky');
            }
        };

        window.addEventListener('scroll', scrollNavigation, true);
        window.addEventListener("click", outerSideElement);
        window.addEventListener("resize", handleResizeLayout);

        return () => {
            window.removeEventListener('scroll', scrollNavigation, true);
            window.removeEventListener("click", outerSideElement);
            window.removeEventListener("resize", handleResizeLayout);
        };
    }, [layoutType, dispatch]);

    const selectProperties = createSelector(
        (state: any) => state.Profile,
        (profile) => ({
            user: profile.user
        })
    );

    const { user } = useSelector(selectProperties);

    useEffect(() => {
        const notificationPermission = localStorage.getItem('notificationPermission');

        if (!notificationPermission) {
            Notification.requestPermission().then(permission => {
                localStorage.setItem('notificationPermission', permission);
            });
        }
    }, []);

    const showNotification = (title: string, options?: NotificationOptions) => {
        if (Notification.permission === "granted") {
            new Notification(title, options);
        }
    };

    useEffect(() => {
        if (Notification.permission !== "granted") return;

        const getDelayToNextTime = (hour: number, minute: number) => {
            const now = new Date();
            const utc = now.getTime() + now.getTimezoneOffset() * 60000;
            const istOffset = 5.5 * 60 * 60000;
            const istNow = new Date(utc + istOffset);
            let nextTime = new Date(istNow);
            nextTime.setHours(hour, minute, 0, 0);
            if (istNow > nextTime) {
                nextTime.setDate(nextTime.getDate() + 1);
            }
            return nextTime.getTime() - istNow.getTime();
        };

        const scheduleNotification = (
            title: string,
            body: string,
            hour: number,
            minute: number
        ) => {
            const delay = getDelayToNextTime(hour, minute);
            const timeoutId = setTimeout(function notify() {
                const now = new Date();
                const utc = now.getTime() + now.getTimezoneOffset() * 60000;
                const istOffset = 5.5 * 60 * 60000;
                const istNow = new Date(utc + istOffset);

                if (istNow.getDay() !== 0) {
                    new Notification(title, { body });
                }

                setTimeout(notify, 24 * 60 * 60 * 1000);
            }, delay);
            return timeoutId;
        };

        const notif10AM = scheduleNotification(
            "Good Morning!",
            "Stay focused and have a productive day ahead.",
            10,
            0
        );

        const notif7PM = scheduleNotification(
            "End of Day Reminder",
            "It's 7:00 PM — wrap up your tasks and plan for tomorrow.",
            19,
            0
        );

        return () => {
            clearTimeout(notif10AM);
            clearTimeout(notif7PM);
        };
    }, []);

    return (
        <React.Fragment>
            <header id="page-topbar" className="ltr:md:left-vertical-menu rtl:md:right-vertical-menu group-data-[sidebar-size=md]:ltr:md:left-vertical-menu-md group-data-[sidebar-size=md]:rtl:md:right-vertical-menu-md group-data-[sidebar-size=sm]:ltr:md:left-vertical-menu-sm group-data-[sidebar-size=sm]:rtl:md:right-vertical-menu-sm group-data-[layout=horizontal]:ltr:left-0 group-data-[layout=horizontal]:rtl:right-0 fixed right-0 z-[1000] left-0 print:hidden group-data-[navbar=bordered]:m-4 group-data-[navbar=bordered]:[&.is-sticky]:mt-0 transition-all ease-linear duration-300 group-data-[navbar=hidden]:hidden group-data-[navbar=scroll]:absolute group/topbar group-data-[layout=horizontal]:z-[1004]">
                <div className="layout-width">
                    <div className="flex items-center px-6 mx-auto bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 group-data-[topbar=dark]:bg-slate-800 group-data-[topbar=dark]:border-slate-700 group-data-[topbar=brand]:bg-blue-600 group-data-[topbar=brand]:border-blue-500 shadow-lg h-header shadow-slate-200/20 dark:shadow-slate-900/20 group-data-[navbar=bordered]:rounded-xl group-data-[navbar=bordered]:group-[.is-sticky]/topbar:rounded-t-none group-data-[topbar=dark]:dark:shadow-slate-900/30 group-data-[topbar=dark]:group-[.is-sticky]/topbar:dark:shadow-lg group-data-[navbar=bordered]:shadow-none group-data-[layout=horizontal]:group-data-[navbar=bordered]:rounded-b-none group-data-[layout=horizontal]:shadow-none group-data-[layout=horizontal]:dark:group-[.is-sticky]/topbar:shadow-none">
                        <div className="flex items-center w-full group-data-[layout=horizontal]:mx-auto group-data-[layout=horizontal]:max-w-screen-2xl navbar-header group-data-[layout=horizontal]:ltr:xl:pr-3 group-data-[layout=horizontal]:rtl:xl:pl-3">

                            <div className="items-center justify-center hidden px-5 text-center h-header group-data-[layout=horizontal]:md:flex group-data-[layout=horizontal]:ltr::pl-0 group-data-[layout=horizontal]:rtl:pr-0">
                                <Link to="/">
                                    {companyLogoWide && !logoError ? (
                                        <img 
                                            src={companyLogoWide} 
                                            alt={companyName || "Company Logo"} 
                                            className="h-8 mx-auto object-contain"
                                            onError={() => setLogoError(true)}
                                        />
                                    ) : (
                                        <>
                                            <span className="hidden">
                                                <img src={logoSm} alt="" className="h-6 mx-auto" />
                                            </span>
                                            <span className="group-data-[topbar=dark]:hidden group-data-[topbar=brand]:hidden">
                                                <img src={logoDark} alt="" className="h-6 mx-auto" />
                                            </span>
                                        </>
                                    )}
                                </Link>
                                <Link to="/" className="hidden group-data-[topbar=dark]:block group-data-[topbar=brand]:block">
                                    {companyLogoWide && !logoError ? (
                                        <img 
                                            src={companyLogoWide} 
                                            alt={companyName || "Company Logo"} 
                                            className="h-8 mx-auto object-contain"
                                            onError={() => setLogoError(true)}
                                        />
                                    ) : (
                                        <>
                                            <span className="group-data-[topbar=dark]:hidden group-data-[topbar=brand]:hidden">
                                                <img src={logoSm} alt="" className="h-6 mx-auto" />
                                            </span>
                                            <span className="group-data-[topbar=dark]:block group-data-[topbar=brand]:block">
                                                <img src={logoLight} alt="" className="h-6 mx-auto" />
                                            </span>
                                        </>
                                    )}
                                </Link>
                            </div>

                            <button
                                onClick={handleTopbarHamburgerIcon}
                                type="button"
                                className="inline-flex relative justify-center items-center p-2 text-slate-700 dark:text-slate-200 transition-all size-[40px] duration-200 ease-out bg-white dark:bg-slate-700 rounded-lg btn hover:bg-slate-100 dark:hover:bg-slate-600 hover:scale-105 active:scale-95 group-data-[topbar=dark]:bg-slate-700 group-data-[topbar=dark]:text-slate-200 group-data-[topbar=dark]:hover:bg-slate-600 group-data-[topbar=brand]:bg-blue-500 group-data-[topbar=brand]:text-white group-data-[topbar=brand]:hover:bg-blue-600 group-data-[topbar=dark]:dark:bg-slate-700 group-data-[topbar=dark]:dark:text-slate-200 group-data-[topbar=dark]:dark:hover:bg-slate-600 group-data-[layout=horizontal]:flex group-data-[layout=horizontal]:md:hidden hamburger-icon shadow-sm hover:shadow-md border border-slate-200 dark:border-slate-600" id="topnav-hamburger-icon">
                                <ChevronsLeft className="w-5 h-5 group-data-[sidebar-size=sm]:hidden transition-transform duration-200" />
                                <ChevronsRight className="hidden w-5 h-5 group-data-[sidebar-size=sm]:block transition-transform duration-200" />
                            </button>

                            <div className="relative hidden ltr:ml-3 rtl:mr-3 lg:block group-data-[layout=horizontal]:hidden group-data-[layout=horizontal]:lg:block">
                                <DateTimeDisplay />
                            </div>

                            <div className="flex gap-3 ms-auto items-center">
                                {/* Availability Toggle (temporarily hidden) */}
                                {/* <AvailabilityToggle /> */}

                                {/* Upcoming Meetings */}
                                <UpcomingMeetings />

                                {/* Schedule Call (temporarily hidden) */}
                                {/*
                                <button
                                    onClick={() => setShowScheduleModal(true)}
                                    type="button"
                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors shadow-sm hover:shadow-md"
                                    title="Schedule a Call"
                                >
                                    <Video className="w-4 h-4" />
                                    <span className="hidden sm:inline">Schedule Call</span>
                                </button>
                                */}

                                <LightDark />

                                <NotificationDropdown />

                                <Dropdown className="relative flex items-center h-header">
                                    <Dropdown.Trigger type="button" className="inline-block p-1 transition-all duration-200 ease-out bg-white dark:bg-slate-700 rounded-full text-slate-700 dark:text-slate-200 dropdown-toggle btn hover:bg-slate-100 dark:hover:bg-slate-600 hover:scale-105 active:scale-95 group-data-[topbar=dark]:text-slate-200 group-data-[topbar=dark]:bg-slate-700 group-data-[topbar=dark]:hover:bg-slate-600 group-data-[topbar=brand]:bg-blue-500 group-data-[topbar=brand]:hover:bg-blue-600 group-data-[topbar=brand]:text-white group-data-[topbar=dark]:dark:bg-slate-700 group-data-[topbar=dark]:dark:hover:bg-slate-600 group-data-[topbar=brand]:text-white group-data-[topbar=dark]:dark:hover:text-slate-50 group-data-[topbar=dark]:dark:text-slate-200 shadow-sm hover:shadow-md border border-slate-200 dark:border-slate-600" id="dropdownMenuButton" data-bs-toggle="dropdown">
                                        <div className="bg-gradient-to-br from-blue-100 to-purple-100 dark:from-slate-600 dark:to-slate-500 rounded-full p-0.5 ring-2 ring-white dark:ring-slate-700">
                                            <img src={userdata.userPhoto} alt="" className="size-[38px] rounded-full object-cover" />
                                        </div>
                                    </Dropdown.Trigger>
                                    <Dropdown.Content placement="right-end" className="absolute z-50 p-6 ltr:text-left rtl:text-right bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-xl shadow-xl !top-4 dropdown-menu min-w-[16rem] border border-slate-200/50 dark:border-slate-700/50" style={{ right: '0', left: 'auto', transform: 'translateY(54px)' }} aria-labelledby="dropdownMenuButton">
                                        <h6 className="mb-3 text-sm font-medium text-slate-600 dark:text-slate-300">Welcome back!</h6>
                                        <a href="#!" className="flex gap-4 mb-4 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-200">
                                            <div className="relative inline-block shrink-0">
                                                <div className="rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 dark:from-slate-700 dark:to-slate-600 p-1">
                                                    <img src={userdata.userPhoto} alt="" className="size-12 rounded-lg object-cover" />
                                                </div>
                                                <span className="-top-1 ltr:-right-1 rtl:-left-1 absolute size-3 bg-green-400 border-2 border-white dark:border-slate-800 rounded-full shadow-sm"></span>
                                            </div>
                                            <div className="flex flex-col justify-center">
                                                <h6 className="mb-1 text-base font-semibold text-slate-800 dark:text-slate-200">{userdata.firstName} {userdata.lastName}</h6>
                                                <p className="text-sm text-slate-500 dark:text-slate-400">{userdata.userRole}</p>
                                            </div>
                                        </a>
                                        <ul className="space-y-1">
                                            <li className="pt-2 mt-2 border-t border-slate-200 dark:border-slate-600">
                                                <a className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-out text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-blue-600 dark:hover:text-blue-400" href="/employees/profile"><User className="size-4"></User> My Profile</a>
                                            </li>
                                            <li>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowLogoutConfirm(true)}
                                                    className="w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-out text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-red-600 dark:hover:text-red-400"
                                                >
                                                    <LogOut className="size-4"></LogOut> Sign Out
                                                </button>
                                            </li>
                                        </ul>
                                    </Dropdown.Content>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Logout Confirmation Modal */}
            <Modal
                show={showLogoutConfirm}
                onHide={() => setShowLogoutConfirm(false)}
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen max-w-md bg-white shadow-xl rounded-lg dark:bg-zink-600 overflow-hidden"
            >
                <Modal.Header
                    className="flex items-center justify-between p-4 border-b dark:border-zink-500"
                    closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
                >
                    <Modal.Title className="text-lg font-semibold">Confirm logout</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-sm text-slate-600 dark:text-zink-200">
                        Are you sure you want to sign out?
                    </p>
                </Modal.Body>
                <Modal.Footer className="flex items-center justify-end gap-2 p-4 border-t dark:border-zink-500">
                    <button
                        type="button"
                        onClick={() => setShowLogoutConfirm(false)}
                        className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-zink-200 bg-white dark:bg-zink-700 border border-slate-300 dark:border-zink-500 rounded-lg hover:bg-slate-50 dark:hover:bg-zink-600"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirmLogout}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                        Sign out
                    </button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
}

export default Header;
